import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PATIENT_DOMAIN,
  PATIENT_LIST_ENDPOINT,
  patientApi,
  PatientType,
} from "api/patientApi";

export default function useEnableTaskUpdateRequired(patient: PatientType) {
  const [modalOpen, setModalOpen] = React.useState(false);

  const {
    patientId,
    isTaskUpdateRequired,
    unreadMessageCount,
    isHumanCheckRequired,
    isRiskCheckRequired,
    isExpirationNoticeRequired,
  } = patient;

  const higherPriorityLabel = React.useMemo(() => {
    if ((unreadMessageCount || 0) > 0) return "채팅 확인 필요";
    if (isHumanCheckRequired) return "휴먼 체크 필요";
    if (isRiskCheckRequired) return "위험 체크 필요";
    if (isExpirationNoticeRequired) return "만료 안내 필요";
    return undefined;
  }, [
    unreadMessageCount,
    isHumanCheckRequired,
    isRiskCheckRequired,
    isExpirationNoticeRequired,
  ]);

  const allowed = !isTaskUpdateRequired && !higherPriorityLabel;

  const { modalTitle, modalSubtitle } = React.useMemo(() => {
    if (higherPriorityLabel) {
      return {
        modalTitle: "업데이트 필요 목록에 넣을 수 없어요" as React.ReactNode,
        modalSubtitle:
          `${higherPriorityLabel} 목록에서 먼저 처리해주세요.` as React.ReactNode,
      };
    }

    if (isTaskUpdateRequired) {
      return {
        modalTitle: "이미 목록에 들어가 있어요" as React.ReactNode,
        modalSubtitle: "업데이트 필요 목록을 확인해보세요." as React.ReactNode,
      };
    }

    return { modalTitle: "", modalSubtitle: "" } as {
      modalTitle: React.ReactNode;
      modalSubtitle: React.ReactNode;
    };
  }, [isTaskUpdateRequired, higherPriorityLabel]);

  const queryClient = useQueryClient();

  const { mutate: enable } = useMutation(
    () => patientApi.enableTaskUpdateRequired(patientId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PATIENT_DOMAIN, PATIENT_LIST_ENDPOINT]);
      },
    }
  );

  return {
    allowed,
    modalOpen,
    setModalOpen,
    modalTitle,
    modalSubtitle,
    enable,
  };
}
